/**
 * M95 — reusable wave / group builders producing StageWaveSpec values.
 * Campaign route policy: AIR groups use ground-* routeIds or omit routeId for distributeRoutes.
 */

import type {
  StageWaveGroupSpec,
  StageWaveSpec
} from '../../combat/campaign/types'
import type { EnemyRank } from '../../combat/types/primitives'
import { ENEMY_CATALOG, enemyMovementClass } from '../../model/enemies'
import { isDedicatedAirRouteId } from '../airNavigation'
import type { LevelArcPhaseId, LevelPlan } from './levelPlan'
import { arcPhaseForWave } from './levelPlan'

export type GroupCompositionInput = {
  enemyId: string
  enemyLevel: number
  count: number
  interval: number
  delay: number
  enemyRank?: EnemyRank
  routeId?: string
}

/** Stable wave id prefix for Chapter 2 (`c2s8-w12`). */
export function chapter2WaveId(stageOrdinal: number, waveIndex: number): string {
  return `c2s${stageOrdinal}-w${waveIndex}`
}

/**
 * Campaign-safe group spec. Throws on dedicated `air-*` routes or non-ground routes on AIR enemies.
 */
export function g(input: GroupCompositionInput): StageWaveGroupSpec
export function g(
  enemyId: string,
  enemyLevel: number,
  count: number,
  interval: number,
  delay: number,
  enemyRank?: EnemyRank,
  routeId?: string
): StageWaveGroupSpec
export function g(
  enemyIdOrInput: string | GroupCompositionInput,
  enemyLevel?: number,
  count?: number,
  interval?: number,
  delay?: number,
  enemyRank: EnemyRank = 'NORMAL',
  routeId?: string
): StageWaveGroupSpec {
  const spec: GroupCompositionInput =
    typeof enemyIdOrInput === 'string'
      ? {
          enemyId: enemyIdOrInput,
          enemyLevel: enemyLevel!,
          count: count!,
          interval: interval!,
          delay: delay!,
          enemyRank,
          routeId
        }
      : enemyIdOrInput

  assertCampaignRoutePolicy(spec.enemyId, spec.routeId)

  const group: StageWaveGroupSpec = {
    enemyId: spec.enemyId,
    enemyLevel: spec.enemyLevel,
    enemyRank: spec.enemyRank ?? 'NORMAL',
    count: spec.count,
    interval: spec.interval,
    delay: spec.delay
  }
  if (spec.routeId) group.routeId = spec.routeId
  return group
}

export function w(
  id: string,
  completionReward: number,
  groups: StageWaveGroupSpec[]
): StageWaveSpec {
  return { id, completionReward, groups }
}

export function assertCampaignRoutePolicy(
  enemyId: string,
  routeId?: string
): void {
  if (!routeId) return
  if (isDedicatedAirRouteId(routeId)) {
    throw new Error(
      `[authoring] routeId "${routeId}" is a dedicated air corridor; use ground-* or omit for distributeRoutes`
    )
  }
  const def = ENEMY_CATALOG[enemyId]
  if (!def) return
  if (enemyMovementClass(def) === 'AIR' && !routeId.startsWith('ground-')) {
    throw new Error(
      `[authoring] AIR enemy "${enemyId}" must use a ground-* routeId or omit routeId`
    )
  }
}

/** Bind an explicit ground lane for multi-route maps; still valid for AIR (physical path). */
export function onGroundLane(
  group: StageWaveGroupSpec,
  routeId: `ground-${string}`
): StageWaveGroupSpec {
  assertCampaignRoutePolicy(group.enemyId, routeId)
  return { ...group, routeId }
}

/** 1-based wave indices covered by an arc phase on a plan. */
export function phaseWaveIndices(
  plan: LevelPlan,
  phaseId: LevelArcPhaseId
): number[] {
  const phase = plan.arcPhases.find((p) => p.id === phaseId)
  if (!phase) return []
  const out: number[] = []
  for (let i = phase.waveStart; i <= phase.waveEnd; i += 1) out.push(i)
  return out
}

/** Lookup arc phase id for a wave (convenience for authored tables). */
export function phaseIdForWave(
  plan: LevelPlan,
  waveIndex: number
): LevelArcPhaseId {
  return arcPhaseForWave(plan, waveIndex)?.id ?? 'develop'
}

/**
 * Offset delays so groups overlap within a wave — deterministic stagger from index.
 */
export function overlapGroups(
  groups: StageWaveGroupSpec[],
  staggerSeconds = 0.4
): StageWaveGroupSpec[] {
  return groups.map((grp, i) => ({
    ...grp,
    delay: grp.delay + i * staggerSeconds
  }))
}

/** Split a total count across two timings (front-loaded + trailing pack). */
export function splitCountPack(
  enemyId: string,
  enemyLevel: number,
  totalCount: number,
  interval: number,
  enemyRank: EnemyRank = 'NORMAL'
): StageWaveGroupSpec[] {
  const first = Math.ceil(totalCount / 2)
  const second = totalCount - first
  const out: StageWaveGroupSpec[] = [
    g(enemyId, enemyLevel, first, interval, 0.25, enemyRank)
  ]
  if (second > 0) {
    out.push(g(enemyId, enemyLevel, second, interval, 0.85, enemyRank))
  }
  return out
}

/** Boss wave helper — zero interval single spawn with support groups appended. */
export function bossWaveGroups(
  bossEnemyId: string,
  bossLevel: number,
  support: StageWaveGroupSpec[],
  bossDelay = 0.35
): StageWaveGroupSpec[] {
  return [g(bossEnemyId, bossLevel, 1, 0, bossDelay), ...support]
}
